import React, { useEffect } from 'react';

import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useStagedFilters } from '../../context/PendingFiltersContext';

import FiltersPanel from './FiltersPanel';

const FLYOUT_WIDTH = 'min(400px, 90vw)';

/* We split up the overlay into two sections so that the part where the flyout
 * is does not flash the custom cursor while flyout is opening.
 */

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
`;
const OverlayBehindFlyout = styled(Overlay)`
  width: ${FLYOUT_WIDTH};
`;

const OverlayBesideFlyout = styled(Overlay)`
  left: ${FLYOUT_WIDTH};
  cursor:
    url('/static/svg/OverlayModalCursor.svg') 16 16,
    auto;
`;

const Flyout = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${FLYOUT_WIDTH};
  background-color: white;
  z-index: 1000;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
`;

interface FiltersPanelModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeFacetName?: string | null;
}

const FiltersPanelModal = ({
  isOpen,
  onClose,
  activeFacetName,
}: FiltersPanelModalProps): React.ReactElement => {
  const { resetToUrl } = useStagedFilters();

  useEffect(() => {
    if (isOpen) {
      // Compensate for scrollbar width to prevent layout shift.
      // A plain `overflow: hidden` removes the scrollbar, which changes
      // document.body.clientHeight and fires a scroll event that the Sticky
      // header's autoHide interprets as a scroll-down, hiding the header.
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const handleOverlayClick = () => {
    resetToUrl();
    onClose();
  };

  return (
    <>
      {createPortal(
        <>
          <OverlayBehindFlyout $isOpen={isOpen} />
          <OverlayBesideFlyout $isOpen={isOpen} onClick={handleOverlayClick} />
          <Flyout $isOpen={isOpen}>
            <FiltersPanel
              closeFiltersPanel={onClose}
              activeFacetName={activeFacetName}
            />
          </Flyout>
        </>,
        document.body
      )}
    </>
  );
};

export default FiltersPanelModal;
